import { addError } from "@wayflyer/blackbox-fe";
import * as React from "react";
import { createIntl, createIntlCache, type IntlShape } from "react-intl";

import { requestPost } from "../request";

import { useMultiFileUploadReducer, type BaseUploadedFile } from "./multi-file-upload-reducer";

import { uploadDocument } from ".";

type DocumentUploadParams<TDocumentType extends string = string> = {
  id?: number;
  documentType?: TDocumentType;
};

const stubIntl = createIntl(
  {
    locale: "en",
    messages: {},
  },
  createIntlCache()
);

const noOp: () => void = () => {};

const RejectCodeToErrorCode = {
  "file-too-large": "FileSizeException",
  "file-invalid-type": "FileTypeNotAllowedException",
};

const genericUploadFailureMessage = (intl: IntlShape) =>
  intl.formatMessage({
    id: "upload manager: on failure",
    defaultMessage: "Upload failed - please contact support if the problem persists",
  });

/**
 * Codes the server returns when it rejects the file the user picked. These are user-input
 * validation, not application faults, so they get a specific message and are never reported to
 * blackbox - matching how dropzone-rejected files are handled in `onFilesAdded` below.
 */
const FileUploadErrorCodes = {
  FileTypeNotAllowedException: ({ intl, supportedFileExtensions }) =>
    supportedFileExtensions
      ? intl.formatMessage(
          {
            id: "upload manager: Invalid file type",
            defaultMessage: "Invalid file type. Supported files: {supportedFileExtensions}",
          },
          { supportedFileExtensions: supportedFileExtensions.join(", ") }
        )
      : genericUploadFailureMessage(intl),
  FileSizeException: ({ intl, sizeLimitMB }) =>
    sizeLimitMB !== undefined
      ? intl.formatMessage(
          {
            id: "upload manager: File size limit exceeded",
            defaultMessage: "File size exceeds limit of {sizeLimitMB}",
          },
          { sizeLimitMB: `${sizeLimitMB}MB` }
        )
      : genericUploadFailureMessage(intl),
};

type UploadManagerOptions<TUploadedFile, TDocumentType extends string = string> = {
  createUrlPath: string;
  confirmUploadPath: string;
  deleteUploadPath: string;
  downloadUploadPath?: string;
  supportedFileExtensions?: string[];
  sizeLimitMB?: number;
  factoryDocumentType?: TDocumentType;
  onUploadsComplete?: () => void;
  onRemoveComplete?: () => void;
  onUploadSuccess?: () => void;
  onUploadFailed?: () => void;
  onRemoveFailed?: () => void;
  files: TUploadedFile[] | Record<string, TUploadedFile[]>;
  /**
   * bring your own axios instance
   *
   * @example
   * (url, body) => client.post(url.toString(), body).then((r) => r.data);
   */
  requestFn?: typeof requestPost;
  /**
   * When set, progress is simulated with these steps (e.g. [0, 25, 50, 100]) instead of real upload progress.
   * Useful for demos and manual testing so the progress bar is clearly visible.
   */
  simulateProgressSteps?: number[];
};

export function uploadManagerHookFactory<TUploadedFile extends BaseUploadedFile, TDocumentType extends string = string>(
  {
    files,
    createUrlPath,
    confirmUploadPath,
    deleteUploadPath,
    downloadUploadPath,
    supportedFileExtensions,
    sizeLimitMB,
    factoryDocumentType, // optional: undefined if not provided
    onUploadsComplete = noOp,
    onRemoveComplete = noOp,
    onUploadSuccess = noOp,
    onUploadFailed = noOp,
    onRemoveFailed = noOp,
    requestFn = requestPost,
    simulateProgressSteps,
  }: UploadManagerOptions<TUploadedFile, TDocumentType>,
  intl: IntlShape = stubIntl
) {
  return function useDocumentUpload({
    id,
    documentType: overrideDocumentType,
  }: DocumentUploadParams<TDocumentType> = {}) {
    const [uploadsState, dispatch] = useMultiFileUploadReducer<TUploadedFile>(id ? files[id] : files);
    const progressTimeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
    const removedFilesRef = React.useRef(new WeakSet<File>());

    React.useEffect(() => {
      if (uploadsState.uploadsComplete) {
        onUploadsComplete();
      }
    }, [uploadsState.uploadsComplete]);

    // Use the override if passed, otherwise use the factory value (may be undefined)
    const uploadDocumentType = overrideDocumentType ?? factoryDocumentType;

    const uploadFile = React.useCallback(
      (file: File): Promise<TUploadedFile> =>
        uploadDocument(file, {
          createUrlPath,
          confirmUploadPath,
          id,
          documentType: uploadDocumentType,
          requestFn,
          onProgress: simulateProgressSteps
            ? undefined
            : (progress) => dispatch({ type: "uploadProgress", payload: { file, progress } }),
        }),
      [dispatch, id, uploadDocumentType]
    );

    const startFileUpload = React.useCallback(
      async (file) => {
        if (file) {
          dispatch({ type: "uploadStarted", payload: { file } });
          if (simulateProgressSteps?.length) {
            const stepMs = 600;
            progressTimeoutsRef.current = simulateProgressSteps.map((progress, i) =>
              setTimeout(() => dispatch({ type: "uploadProgress", payload: { file, progress } }), i * stepMs)
            );
          }
          let uploadedFile;
          try {
            uploadedFile = await uploadFile(file);
            onUploadSuccess();
            dispatch({ type: "uploadFinished", payload: { file, uploadedFile: uploadedFile as TUploadedFile } });
          } catch (error) {
            const errorCode = typeof error === "string" ? error : (error as any)?.code;
            const isRejectedByServer = !!errorCode && Object.hasOwn(FileUploadErrorCodes, errorCode);
            const messageDetail = isRejectedByServer
              ? FileUploadErrorCodes[errorCode]({ intl, supportedFileExtensions, sizeLimitMB })
              : genericUploadFailureMessage(intl);
            onUploadFailed();
            dispatch({ type: "uploadFailed", payload: { file, error: messageDetail } });
            if (!isRejectedByServer) {
              addError(error);
            }
          } finally {
            progressTimeoutsRef.current.forEach(clearTimeout);
            progressTimeoutsRef.current = [];
          }
        }
      },
      [dispatch, uploadFile]
    );

    const onFilesAdded = React.useCallback(
      (files, rejectedFiles) => {
        dispatch({ type: "filesAdded", payload: { files } });
        for (const file of files) {
          startFileUpload(file);
        }

        const hasValidationErrors = !!rejectedFiles.length;

        if (hasValidationErrors) {
          dispatch({ type: "filesAdded", payload: { files: rejectedFiles.map(({ file }) => file) } });

          for (const { file, errors } of rejectedFiles) {
            // files rejected by dropzone, user error so supress log
            const errorCode = RejectCodeToErrorCode[errors[0].code]
              ? RejectCodeToErrorCode[errors[0].code]
              : errors[0].code;
            const messageDetail = FileUploadErrorCodes[errorCode]
              ? FileUploadErrorCodes[errorCode]({ intl, supportedFileExtensions, sizeLimitMB })
              : genericUploadFailureMessage(intl);

            dispatch({ type: "uploadFailed", payload: { file, error: messageDetail } });
          }

          onUploadFailed();
        }
      },
      [dispatch, startFileUpload]
    );

    const onFileRemoved = React.useCallback(
      async (file, uploadedFile) => {
        if (uploadedFile) {
          // A repeat click on a file already being (or already) deleted would 404 and show a removal failure.
          if (removedFilesRef.current.has(file)) {
            return;
          }
          removedFilesRef.current.add(file);
          try {
            await requestFn(deleteUploadPath, { key: uploadedFile.objectKey, id });

            dispatch({ type: "fileRemoved", payload: { file } });
            onRemoveComplete();
          } catch (error) {
            removedFilesRef.current.delete(file);
            addError(error);

            const errorDetails = intl.formatMessage({
              id: "upload manager: on remove failure",
              defaultMessage: "File removal failed - please contact support if the problem persists",
            });

            dispatch({ type: "removeFailed", payload: { file, error: errorDetails } });
            onRemoveFailed();
          }
        } else {
          dispatch({ type: "fileRemoved", payload: { file } });
        }
      },
      [dispatch, id]
    );

    const onFileDownload = React.useCallback(async (params) => {
      if (params && downloadUploadPath) {
        const { url } = await requestFn(downloadUploadPath, params);
        window.open(url, "_blank");
      }
    }, []);

    const onFilesChanged = React.useCallback(() => dispatch({ type: "reset" }), [dispatch]);

    return {
      uploadsState,
      onFilesAdded,
      onFileDownload,
      onFileRemoved,
      supportedFileExtensions,
      sizeLimitMB,
      onFilesChanged,
    };
  };
}
